import { Sparkles } from 'lucide-react';

// The read above the tiles. It is the only part of the dashboard that says
// something rather than showing something, so it gets the top of the page —
// but it stays visually quiet: a wrong-looking hero block would cost more
// trust than the paragraph earns.
//
// It renders nothing at all when the backend has nothing honest to say (too
// few figures, or the model was unavailable). A missing narrative must never
// look like a broken dashboard.

export interface NarrativeData {
  text: string;
  period?: string | null;
  facts_used?: number;
  cached?: boolean;
}

export default function Narrative({
  data,
  loading
}: {
  data: NarrativeData | null;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="mb-4 rounded-lg border bg-muted/30 px-4 py-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5 animate-pulse" />
          Reading the numbers…
        </div>
      </div>
    );
  }
  if (!data?.text) return null;

  // The server writes this in English now. The check stays so a paragraph
  // cached in Arabic before 2026-09-12, or a tenant name inside an English
  // sentence, still lays out the right way round.
  const isArabic = /[؀-ۿ]/.test(data.text);

  return (
    <div className="mb-4 rounded-lg border border-primary/20 bg-primary/[0.04] px-4 py-3">
      <div className="flex items-center gap-2 mb-1.5">
        <Sparkles className="h-3.5 w-3.5 text-primary" />
        <span className="text-xs font-medium text-primary">What this says</span>
        {data.period && (
          <span className="text-xs text-muted-foreground">· {data.period}</span>
        )}
      </div>
      <p
        dir={isArabic ? 'rtl' : 'ltr'}
        className={`text-sm leading-relaxed text-foreground ${
          isArabic ? 'text-right' : ''
        }`}
      >
        {data.text}
      </p>
      <div className="mt-1.5 text-[11px] text-muted-foreground">
        Written from the figures on this page and the findings of last night's
        check — no number here comes from anywhere else.
      </div>
    </div>
  );
}
